/// <reference path="../../../../typings/index.d.ts" />
/// <reference path="inventoryTransfer.module.ts" />
/// <reference path="../../components/common/lotModalBridge.helper.ts" />

module Meevo.Plugins.InventoryTransfer {

    /**
     * Input shape for the <meevo-lot-tracking-modal> custom element's `lineItem`
     * property when invoked from the Inventory Transfer flow. Mirrors the
     * `InventoryTransferLineItem` arm of the web component's `LotTrackingLineItem`
     * discriminated union.
     *
     * `context: 'inventory-transfer'` is the union discriminator. The web component
     * narrows on this field; a new arm in the WC is required for the modal to
     * actually render the inventory-transfer-specific layout (see FA8 plan §F3 /
     * Open Items — flagged to the orchestrator as a paired WC work item).
     */
    export interface ILotModalInput {
        context: 'inventory-transfer';
        variantId: string;
        productInventoryTypeEnum: number;
        /** Per-FA8 the From location drives the typeahead's on-hand filter. May
         *  differ from the user's effective location (CO-initiated transfers). */
        fromLocationId: number;
        /** Header subtitle line for Transfer Out view: product + variant. */
        productDisplayName: string;
        variantDisplayName: string;
        sku: string;
        barCode: string;
        inventoryTypeDisplay: string;
        trackExpirationDate: boolean;
        /** Total transfer quantity at modal-open time. The WC's editable mode
         *  treats this as informational (the sum of allocations supersedes); the
         *  readonly mode renders it as "Qty Transferred: {N}" in the subtitle
         *  per the FA8 Receive variant. */
        transferQuantity: number;
        /** Optional Autofill hint rendered as a muted line at the top of the
         *  editable modal. Cleared by the host once the user applies an
         *  allocation. */
        autofillSuggestedQty?: number;
        /** True when the modal is being opened from the Receive screen. The WC
         *  uses this together with mode='readonly' to swap the subtitle from
         *  "{product} - {variant}" to "Qty Transferred: {N}". */
        isReceiveView?: boolean;
    }

    /**
     * Allocation shape used for both `existingAllocations` input and the `saved`
     * event payload. Mirrors VariantLotAllocation on the wire.
     *
     * No `_pending` flag here (unlike PO Receive): inventory transfers commit
     * lot allocations atomically with Save / Transfer Out — there is no partial-
     * receive style intermediate state. Every allocation the WC emits goes
     * straight back into `lineItem.lotAllocations` and rides the next Save.
     */
    export interface ILotAllocation {
        variantLot_TId: string;
        lotNumber: string;
        expirationDate: string | null;
        quantity: number;
    }

    /**
     * Thin AngularJS bridge for the <meevo-lot-tracking-modal> Angular 20
     * Elements web component, scoped to the Inventory Transfer flow. Wraps the
     * shared `LotModalBridgeHelper` so this plugin hits the same host-bridge
     * construction, session teardown wiring, and error path as PO Receive +
     * Inventory Count.
     *
     * Inventory Transfer only uses 'editable' or 'readonly' modes — there is no
     * 'partial' state (allocations are committed atomically with the Save /
     * Transfer Out button, never incrementally like PO receivals).
     */
    export class LotModalBridge {

        static $inject = [
            'meevo.components.common.LotModalBridgeHelper',
        ];

        constructor(
            private helper: Meevo.Components.Common.LotModalBridgeHelper,
        ) { }

        /**
         * Mount the lot-tracking-modal for the given inventory-transfer line item.
         *
         * @param lineItem            Variant + transfer-context info (From location, qty, etc.)
         * @param existingAllocations Pre-existing per-lot allocations for the variant.
         * @param mode                'editable' on Transfer Out before the transfer commits;
         *                            'readonly' for the post-transfer eye and the Receive eye.
         * @param onSaved             Invoked with the final allocations array on Apply.
         * @param onCancelled         Invoked when the user cancels (X, Escape, or Cancel).
         */
        public open(
            lineItem: ILotModalInput,
            existingAllocations: ILotAllocation[],
            mode: 'editable' | 'readonly',
            onSaved?: (allocations: ILotAllocation[]) => void,
            onCancelled?: () => void,
        ): void {
            this.helper.openModal<ILotModalInput, ILotAllocation>({
                context: 'inventory-transfer',
                mode: mode,
                lineItem: lineItem,
                domProps: { existingAllocations: existingAllocations },
                onSaved: onSaved,
                onCancelled: onCancelled,
            });
        }
    }

    angular.module('meevo.plugins.inventoryTransfer')
        .service('meevo.plugins.inventoryTransfer.LotModalBridge', LotModalBridge);
}
